/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import classNames from "classnames";
import { includes, isEmpty, last } from "lodash";
import { useMisalignments } from "PFApp/booking/parts/hooks";
import {
  CalendarGranularity,
  CalendarMetric,
  CalendarMode,
  CalendarView,
  Row as RowAsType
} from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import React, { memo, useEffect, useRef, useState } from "react";

import { useBookingFormContext } from "../../../../components/booking_form/booking_form_context_provider";
import { WorkforceCalendarData } from "../../context/booking_overview_context.types";
import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { useCalendarContext } from "../../context/calendar_context";
import { Day, Week } from "../../context/use_calendar_days";
import { NonWorkingDays } from "../days";
import { AvailabilityRow } from "./availability_row";
import { BookingsRow } from "./bookings_row";
import { getRowCalendarsData } from "./helpers";
import css from "./row.module.scss";
import { TotalHoursRow } from "./total_hours_row";
import { UtilizationRow } from "./utilization_row";

type RowComponent =
  | typeof UtilizationRow
  | typeof AvailabilityRow
  | typeof TotalHoursRow
  | typeof BookingsRow;

const getRowComponent = (
  calendarMode: CalendarMode,
  view: CalendarView,
  metric: CalendarMetric,
  rowType: RowType
): RowComponent | null => {
  if (calendarMode === CalendarMode.Projects) {
    return BookingsRow;
  }
  if ([CalendarView.BookingsOnly, CalendarView.Compressed].includes(view) || rowType !== RowType.Profile) {
    return BookingsRow;
  } else {
    switch (metric) {
      case CalendarMetric.Utilization:
        return UtilizationRow;
      case CalendarMetric.Availability:
        return AvailabilityRow;
      case CalendarMetric.TotalHours:
        return TotalHoursRow;
      default:
        return null;
    }
  }
};

export type HoverPeriodData = { period: Day | Week; mouseOffsetTop: number };
type RowProps = {
  row: RowAsType;
  width: number;
  startDate: string;
  endDate: string;
};

export const Row = memo(({ row, width, startDate, endDate }: RowProps): JSX.Element => {
  const { days, weeks } = useCalendarContext();
  const [hoveredPeriodData, setHoveredPeriodData] = useState<HoverPeriodData | null>(null);

  const {
    calendarMode,
    calendarData: { view, metric, data }
  } = useBookingsOverviewContext();
  const {
    calendarData: { calendarGranularity }
  } = useBookingsOverviewContext<WorkforceCalendarData>();
  const { barsByRowKey, rowsByCalendarMode, rowDimensionsByCalendarMode } = useBookingRowsContext();

  const {
    modal: { refreshData: refreshModalData }
  } = useBookingFormContext();
  const { misalignmentBars } = useMisalignments({ row, mode: calendarMode });

  const rowRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [draggedBookingId, setDraggedBookingId] = useState<number | null>(null);
  const dimensions = rowDimensionsByCalendarMode[calendarMode][row.key];

  const rows = rowsByCalendarMode[calendarMode];
  const parentRow = rows.find(({ key }) => last(row.parents) === key);
  const rowCalendarsData = getRowCalendarsData(row, startDate, endDate, calendarMode, parentRow);
  const isRootRow = isEmpty(row.parents);
  const isFirstRow = rows?.[0]?.key === row.key;
  const isLastRow = last(rows)?.key === row.key;

  useEffect(() => {
    refreshModalData({
      searchId: data.meta.search_id,
      total: data.meta.total,
      entries: data.entries
    });
  }, [data.meta.search_id, data.meta.total, data.entries]);

  const isSummary = calendarMode === CalendarMode.Workforce && row.type === RowType.Profile;
  const shouldBarsOverlap =
    (isSummary && view === CalendarView.Compressed) ||
    (Math.max((barsByRowKey[row.key] || []).length, misalignmentBars.length) < 2 &&
      !includes(
        [CalendarMetric.Utilization, CalendarMetric.Availability, CalendarMetric.TotalHours],
        metric
      ));

  const RowComponent = getRowComponent(calendarMode, view, metric, row.type);

  const handleMouseMove = (event) => {
    const target = document.querySelector(`#${row.key}`);
    if (!target) {
      setHoveredPeriodData(null);
      return;
    }
    const mouseDistanceFromCalendarStart = event.clientX - target.getBoundingClientRect().x;
    const mouseDistanceFromRowTop = event.clientY - target.getBoundingClientRect().y;
    let hoveredPeriod: Day | Week | undefined;
    const shouldBaseOnGranularity =
      calendarMode === CalendarMode.Workforce &&
      ![CalendarView.BookingsOnly, CalendarView.Compressed].includes(view);
    if (!shouldBaseOnGranularity || calendarGranularity === CalendarGranularity.Daily) {
      hoveredPeriod = days.find(
        ({ left }, index) =>
          left <= mouseDistanceFromCalendarStart &&
          (!days[index + 1] || days[index + 1]?.left > mouseDistanceFromCalendarStart)
      );
    } else {
      hoveredPeriod = weeks.find(
        ({ left }, index) =>
          left <= mouseDistanceFromCalendarStart &&
          (!weeks[index + 1] || weeks[index + 1]?.left > mouseDistanceFromCalendarStart)
      );
    }
    if (
      hoveredPeriod &&
      (hoveredPeriodData?.period.left !== hoveredPeriod.left ||
        hoveredPeriodData?.mouseOffsetTop !== mouseDistanceFromRowTop)
    ) {
      setHoveredPeriodData({
        period: hoveredPeriod,
        mouseOffsetTop: mouseDistanceFromRowTop
      });
    }
  };

  return (
    <div
      id={row.key}
      ref={rowRef}
      className={classNames(css.row, {
        [css.rowLoading]: loading,
        [css.centered]: shouldBarsOverlap,
        [css.borderTop]: isRootRow && !isFirstRow,
        [css.borderBottom]: isLastRow
      })}
      style={{
        ...(dimensions && {
          top: dimensions.top,
          height: dimensions.height
        }),
        width
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoveredPeriodData(null)}
    >
      {RowComponent && (
        <RowComponent
          row={row}
          ref={rowRef}
          startDate={startDate}
          endDate={endDate}
          onLoadingChange={(value) => setLoading(value)}
          draggedBookingId={draggedBookingId}
          onBarDragStart={(bookingId) => setDraggedBookingId(bookingId)}
          onBarDragEnd={() => setDraggedBookingId(null)}
          shouldBarsOverlap={shouldBarsOverlap}
          hoveredPeriodData={hoveredPeriodData}
        />
      )}
      {rowCalendarsData && (
        <NonWorkingDays rowCalendarsData={rowCalendarsData} days={days} endDate={endDate} />
      )}
    </div>
  );
});

Row.displayName = "Row";
